import { v4 as uuidv4 } from 'uuid'
import countries from 'i18n-iso-countries'
import { proxyException } from '@/utils/communication'
import EventBus from '@/components/mixins/event-bus-mixin'
import _ from 'lodash'

import * as enLocale from 'i18n-iso-countries/langs/en.json'
import * as frLocale from 'i18n-iso-countries/langs/fr.json'

countries.registerLocale(enLocale)
countries.registerLocale(frLocale)

export default {
  name: 'select-city',
  inject: [
    '$utils',
    '$api'
  ],
  props: {
    cityName: {
      type: String,
      default () { return '' }
    }
  },
  watch: {
    cityName (newVal) {
      this.city = newVal
    }
  },
  computed: {
    getAppLocale () {
      return this.$i18n?.locale ?? 'en'
    }
  },
  mixins: [],
  data () {
    return {
      city: '',
      cityOptions: [],
      isSearchingPlaces: false,
      isFetchingPlaceDetails: false,
      searchPlaceSessionToken: null,
      currentRequestsFlag: []
    }
  },
  methods: {
    async searchPlace (name) {
      // Create session token if empty
      if (_.isEmpty(this.searchPlaceSessionToken)) {
        this.searchPlaceSessionToken = uuidv4()
      }

      this.isSearchingPlaces = true
      const { ok: response, error } = await proxyException(() => this.$api.get(`${CONFIG.API_FEEDS_URL}/weather/place/autocomplete/json`, {
        params: {
          input: name,
          type: 'geocode',
          language: this.getAppLocale,
          sessiontoken: this.searchPlaceSessionToken
        }
      }))

      this.isSearchingPlaces = false

      if (error) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.failedToFetchPlaces')
          }
        })
        return
      }

      this.cityOptions = this.predictedCityLocations(response.data?.predictions ?? [])
    },
    predictedCityLocations (predictions) {
      const hasValidType = type => _.includes(['locality', 'sublocality', 'postal_code'], type)

      return _.chain(predictions)
        .filter(item => item.types && _.some(item.types, hasValidType))
        .map(item => _.assign(item, {
          placeId: item.place_id,
          name: item.cityName ?? item.description,
          location: item.location ?? false
        }))
        .value()
    },
    /**
     * Get city name from places address components based on type
      * Priority type
      * 1. sublocality
      * 2. locality
      * 3. postal_town
    */
    getCityNameFromAddressComponents (addressComponents, defaultName) {
      const getCityNameByType = addressComponents.find(item => {
        return item.types.find(type => ['sublocality', 'locality', 'postal_town'].includes(type))
      })
      return !_.isEmpty(getCityNameByType) && getCityNameByType.short_name ? getCityNameByType.short_name : defaultName
    },
    /**
     * Get country name from places address components based on country type
    */
    getCountryNameFromAddressComponents (addressComponents) {
      // Note: short_name is the country code
      const getCountryType = addressComponents.find(item => {
        return item.types.find(type => ['country'].includes(type))
      })
      return !_.isEmpty(getCountryType) && getCountryType.long_name ? getCountryType.long_name : ''
    },
    async getPlaceDetails (place) {
      if (_.isEmpty(this.searchPlaceSessionToken) && place.placeId && place.location) {
        return
      }

      this.isFetchingPlaceDetails = true
      const { ok: response, error } = await proxyException(() => this.$api.get(`${CONFIG.API_FEEDS_URL}/weather/place/details/json`, {
        params: {
          placeid: place.placeId,
          type: 'geocode',
          language: this.getAppLocale,
          sessiontoken: this.searchPlaceSessionToken,
          fields: 'address_component,adr_address,alt_id,formatted_address,geometry,name,type,place_id'
        }
      }))

      this.isFetchingPlaceDetails = false
      if (error) {
        EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
          type: 'error',
          alert: {
            message: error.response ? error.response?.data.message ?? error.message : this.$t('errors.failedToFetchPlaces')
          }
        })
        return
      }

      const responseData = {
        addressComponents: (response.data.result.address_components) ? response.data.result.address_components : [],
        geometry: (response.data.result.geometry) ? response.data.result.geometry : null,
        formattedAddress: (response.data.result.formatted_address) ? response.data.result.formatted_address : null,
        id: (response.data.result.id) ? response.data.result.id : null
      }

      const cityName = this.getCityNameFromAddressComponents(responseData.addressComponents, place.name)
      const countryName = this.getCountryNameFromAddressComponents(responseData.addressComponents)

      return {
        name: cityName,
        cityName,
        countryName,
        fullname: `${cityName}, ${countryName}`,
        description: responseData.formattedAddress,
        geometry: responseData.geometry,
        placeId: place.placeId,
        location: OpenLocationCode.encode(responseData.geometry.location.lat, responseData.geometry.location.lng)
      }
    },
    async updateCityDetails (place) {
      const details = await this.getPlaceDetails(place)
      this.cityOptions = [details]
      this.searchPlaceSessionToken = null
      this.$emit('change', details)

      /**
       * Click the selected item in the selectbox to show the updated name of the City.
       * By default the search Text is the value of the selectbox and it should be replaced when city name is available.
       */
      this.$nextTick(_ => {
        this.selectboxPopperElement().find('.el-select-dropdown__item.selected').on('click', () => {})
        this.$refs.selectCity.blur()
      })
    },
    selectboxPopperElement () {
      return $('.select-city-component-popper').length ? $('.select-city-component-popper') : []
    },
    handleSelectChange (place = {}) {
      this.updateCityDetails(place)
      this.handleSelectboxPopperStyle({ style: { display: 'none' } })
    },
    handleSelectTyping ($event) {
      const inputParentEl = $(this.$refs.selectCity.$el).parent()
      const isFocus = inputParentEl.hasClass('is-focus')

      if (!isFocus) {
        const cityName = $($event.target).val()
        const currentSelectBoxOffset = inputParentEl.parent().offset()
        const leftStyleVal = currentSelectBoxOffset.left
        let topStyleVal = currentSelectBoxOffset.top

        if (this.$utils.is1920View()) {
          topStyleVal += 40
        } else {
          topStyleVal += 25
        }

        this.handleSelectboxPopperStyle({ display: 'block', top: topStyleVal, left: leftStyleVal })
        this.debouncedSearch(cityName)
      }
    },
    handleSelectboxPopperStyle (style = {}) {
      this.selectboxPopperElement().css(style)
    },
    handleCloseSelectPopperOnClick () {
      $(document).on('click', (event) => {
        const targetEl = $(event.target).attr('class')

        if (targetEl && this.selectboxPopperElement().length && !targetEl.includes('el-select-dropdown__item')) {
          this.handleSelectboxPopperStyle({ display: 'none' })
        }
      })
    },
    debouncedSearch: _.debounce(function (query) {
      this.searchPlace(query)
    }, 300)
  },
  mounted () {
    this.city = this.cityName
    this.handleCloseSelectPopperOnClick()
  }
}
